import * as mutations from "../graphql/mutations";
import { get } from "@aws-amplify/api";
import { fetchAuthSession } from "@aws-amplify/auth";
import { apiClient } from "./apiClient";
import {
  fetchModulesByAssessment,
  fetchQuestionsByModule,
  fetchRequirementsByQuestion,
  fetchStructuredEntriesByModule,
  fetchRequirementAnswerByPerformAssessment,
  fetchRecommendationsByRequirement,
  fetchPerformedAssessmentByAssessmentAssignment,
  fetchAssessmentAssignmentByAssessment,
  fetchAllAssessmentAssignmentsByCustomer,
  fetchAssessmentById,
  fetchCustomersAll,
  fetchEvaluationsByAssessmentAssignment,
  fetchEvaluationModuleDataByEvaluation,
} from "./apiRequests";

const adminApiName = "AdminQueries";
const OWNER_GROUP_UPDATE_CONCURRENCY = 8;

const dedupe = (arr = []) => [...new Set(arr.filter(Boolean))];

function hasSameGroups(a = [], b = []) {
  return a.length === b.length && a.every((group) => b.includes(group));
}

async function mapWithConcurrency(items, limit, mapper) {
  const source = items || [];
  const results = [];
  let index = 0;

  async function worker() {
    while (index < source.length) {
      const currentIndex = index;
      index += 1;
      results[currentIndex] = await mapper(source[currentIndex], currentIndex);
    }
  }

  await Promise.all(
    Array.from({ length: Math.min(limit, source.length) }, worker),
  );
  return results;
}

async function updateOwnerGroupsIfChanged(query, id, currentGroups, newGroups) {
  const ownerGroups = dedupe([...(currentGroups ?? []), ...newGroups]);
  if (hasSameGroups(currentGroups ?? [], ownerGroups)) {
    return ownerGroups;
  }

  await apiClient.graphql({
    query,
    variables: {
      input: {
        id,
        ownerGroups,
      },
    },
  });

  return ownerGroups;
}

async function setOwnerGroupsIfChanged(query, id, currentGroups, newGroups) {
  const ownerGroups = dedupe(newGroups);
  if (hasSameGroups(currentGroups ?? [], ownerGroups)) {
    return ownerGroups;
  }

  await apiClient.graphql({
    query,
    variables: {
      input: {
        id,
        ownerGroups,
      },
    },
  });

  return ownerGroups;
}

async function listGroupsOfUser(username) {
  const path = "/listGroupsForUser";

  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();

  const options = {
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    queryParams: {
      username: username,
    },
  };

  const restOperation = await get({
    apiName: adminApiName,
    path,
    options,
  });

  let parsed;
  try {
    const { body } = await restOperation.response;
    parsed = await body.json();
  } catch {
    parsed = restOperation?.data ?? restOperation;
  }

  return parsed.Groups;
}

async function listUniqueGroupsOfUsers(usernames = []) {
  const groupLists = await Promise.all(usernames.map(listGroupsOfUser));
  return dedupe(
    groupLists.flatMap((groupArray) =>
      (groupArray || []).map((group) => group.GroupName),
    ),
  );
}

export async function updateOwnerGroupsForCustomer(customer) {
  try {
    const usernames = customer.usernames || [];
    const uniqueGroups = await listUniqueGroupsOfUsers(usernames);

    await apiClient.graphql({
      query: mutations.updateCustomer,
      variables: {
        input: {
          id: customer.id,
          ownerGroups: uniqueGroups,
        },
      },
    });

    return uniqueGroups;
  } catch (error) {
    console.error("Failed to update ownerGroups for customer", error);
    throw error;
  }
}

async function updateAssessmentContentOwnerGroups(assessmentId, newGroups) {
  const [assessment, modules] = await Promise.all([
    fetchAssessmentById(assessmentId),
    fetchModulesByAssessment(assessmentId),
  ]);

  if (assessment) {
    await updateOwnerGroupsIfChanged(
      mutations.updateAssessment,
      assessment.id,
      assessment.ownerGroups,
      newGroups,
    );
  }

  await mapWithConcurrency(
    modules,
    OWNER_GROUP_UPDATE_CONCURRENCY,
    async (mod) => {
      await updateOwnerGroupsIfChanged(
        mutations.updateModule,
        mod.id,
        mod.ownerGroups,
        newGroups,
      );

      const [structuredEntries, questions] = await Promise.all([
        fetchStructuredEntriesByModule(mod.id),
        fetchQuestionsByModule(mod.id),
      ]);

      await Promise.all([
        mapWithConcurrency(
          structuredEntries,
          OWNER_GROUP_UPDATE_CONCURRENCY,
          (entry) =>
            updateOwnerGroupsIfChanged(
              mutations.updateStructuredEntry,
              entry.id,
              entry.ownerGroups,
              newGroups,
            ),
        ),
        mapWithConcurrency(
          questions,
          OWNER_GROUP_UPDATE_CONCURRENCY,
          async (question) => {
            await updateOwnerGroupsIfChanged(
              mutations.updateQuestion,
              question.id,
              question.ownerGroups,
              newGroups,
            );

            const requirements = await fetchRequirementsByQuestion(question.id);

            await mapWithConcurrency(
              requirements,
              OWNER_GROUP_UPDATE_CONCURRENCY,
              async (req) => {
                await updateOwnerGroupsIfChanged(
                  mutations.updateRequirement,
                  req.id,
                  req.ownerGroups,
                  newGroups,
                );

                const recommendationResult =
                  await fetchRecommendationsByRequirement(req.id);
                const recommendations = recommendationResult?.items ?? [];

                await mapWithConcurrency(
                  recommendations,
                  OWNER_GROUP_UPDATE_CONCURRENCY,
                  (recommendation) =>
                    updateOwnerGroupsIfChanged(
                      mutations.updateRecommendation,
                      recommendation.id,
                      recommendation.ownerGroups,
                      newGroups,
                    ),
                );
              },
            );
          },
        ),
      ]);
    },
  );
}

async function syncAssignmentScopedOwnerGroups(assignment, ownerGroups) {
  await setOwnerGroupsIfChanged(
    mutations.updateAssessmentAssignment,
    assignment.id,
    assignment.ownerGroups,
    ownerGroups,
  );

  const [performResult, evaluationResult] = await Promise.all([
    fetchPerformedAssessmentByAssessmentAssignment(assignment.id),
    fetchEvaluationsByAssessmentAssignment(assignment.id),
  ]);

  await Promise.all([
    mapWithConcurrency(
      performResult?.items ?? [],
      OWNER_GROUP_UPDATE_CONCURRENCY,
      async (perform) => {
        await setOwnerGroupsIfChanged(
          mutations.updatePerformAssessment,
          perform.id,
          perform.ownerGroups,
          ownerGroups,
        );

        const answerResult = await fetchRequirementAnswerByPerformAssessment(
          perform.id,
        );
        const answers = answerResult?.items ?? [];

        await mapWithConcurrency(
          answers,
          OWNER_GROUP_UPDATE_CONCURRENCY,
          (answer) =>
            setOwnerGroupsIfChanged(
              mutations.updateRequirementAnswer,
              answer.id,
              answer.ownerGroups,
              ownerGroups,
            ),
        );
      },
    ),
    mapWithConcurrency(
      evaluationResult?.items ?? [],
      OWNER_GROUP_UPDATE_CONCURRENCY,
      async (evaluation) => {
        await setOwnerGroupsIfChanged(
          mutations.updateEvaluation,
          evaluation.id,
          evaluation.ownerGroups,
          ownerGroups,
        );

        const moduleData =
          (await fetchEvaluationModuleDataByEvaluation(evaluation.id)) ?? [];

        await mapWithConcurrency(
          moduleData,
          OWNER_GROUP_UPDATE_CONCURRENCY,
          (data) =>
            setOwnerGroupsIfChanged(
              mutations.updateEvaluationModuleData,
              data.id,
              data.ownerGroups,
              ownerGroups,
            ),
        );
      },
    ),
  ]);
}

export async function syncOwnerGroupsForCustomer(customer) {
  const ownerGroups = await listUniqueGroupsOfUsers(customer.usernames || []);

  await setOwnerGroupsIfChanged(
    mutations.updateCustomer,
    customer.id,
    customer.ownerGroups,
    ownerGroups,
  );

  const assignments = await fetchAllAssessmentAssignmentsByCustomer(customer.id);

  await mapWithConcurrency(
    assignments,
    OWNER_GROUP_UPDATE_CONCURRENCY,
    async (assignment) => {
      await Promise.all([
        updateAssessmentContentOwnerGroups(assignment.assessmentID, ownerGroups),
        syncAssignmentScopedOwnerGroups(assignment, ownerGroups),
      ]);
    },
  );

  return ownerGroups;
}

export async function syncOwnerGroupsForUser(username) {
  try {
    const customers = await fetchCustomersAll();
    const affectedCustomers = (customers || []).filter((customer) =>
      (customer.usernames || []).includes(username),
    );

    await mapWithConcurrency(
      affectedCustomers,
      OWNER_GROUP_UPDATE_CONCURRENCY,
      syncOwnerGroupsForCustomer,
    );
  } catch (error) {
    console.error("Failed to sync owner groups for user", error);
    throw error;
  }
}

export async function updateOwnerGroupsForAssessmentAndRelated(
  assessmentId,
  newGroups
) {
  try {
    const assignments = await fetchAssessmentAssignmentByAssessment(assessmentId);
    await updateAssessmentContentOwnerGroups(assessmentId, newGroups);

    await mapWithConcurrency(
      assignments,
      OWNER_GROUP_UPDATE_CONCURRENCY,
      async (assignment) => {
        await updateOwnerGroupsIfChanged(
          mutations.updateAssessmentAssignment,
          assignment.id,
          assignment.ownerGroups,
          newGroups,
        );

        const performResult =
          await fetchPerformedAssessmentByAssessmentAssignment(assignment.id);
        const performList = performResult?.items ?? [];

        await Promise.all([
          mapWithConcurrency(
            performList,
            OWNER_GROUP_UPDATE_CONCURRENCY,
            (perform) =>
              updateOwnerGroupsIfChanged(
                mutations.updatePerformAssessment,
                perform.id,
                perform.ownerGroups,
                newGroups,
              ),
          ),
          syncAssignmentScopedOwnerGroups(assignment, [
            ...(assignment.ownerGroups ?? []),
            ...newGroups,
          ]),
        ]);
      },
    );
  } catch (error) {
    console.error(error);
  }
}
